import { z } from 'zod';

export const PLAN_SCHEMA_VERSION = '2026-07-13' as const;

/**
 * The harness's specialist agents. `researcher` handles broad external
 * research; `summarizer` condenses previous agent outputs into a final
 * TL;DR; the remaining agents produce analysis, drafts, edits, or repository
 * search results. Every agent produces user-visible text.
 *
 * `task` is special: it only appears in plans for threads bound to a
 * workflow task. It owns the task-state mutation tools (comment, reassign,
 * complete, fail) so no other subagent can prematurely change task state.
 * The planner must only schedule `task` when the thread has a bound task;
 * the executor enforces this at runtime via `taskExtras` on `NodeRunContext`.
 */
export const HARNESS_AGENT_NAMES = {
  analyzer: 'analyzer',
  drafter: 'drafter',
  editor: 'editor',
  playbook: 'playbook',
  report: 'report',
  repository: 'repository',
  researcher: 'researcher',
  summarizer: 'summarizer',
  task: 'task',
  workflow: 'workflow',
} as const;
export const HarnessAgentNameSchema = z.enum(HARNESS_AGENT_NAMES);
export type HarnessAgentName = (typeof HARNESS_AGENT_NAMES)[keyof typeof HARNESS_AGENT_NAMES];

export const PLAN_STAGE_TYPES = {
  parallel: 'parallel',
  single: 'single',
} as const;
export type PlanStageType = (typeof PLAN_STAGE_TYPES)[keyof typeof PLAN_STAGE_TYPES];

export const SuccessCriterionSchema = z.object({
  description: z.string().min(1),
  id: z.string().min(1),
});
export type SuccessCriterion = z.infer<typeof SuccessCriterionSchema>;

const PlanTitleSchema = z.string().trim().min(1).max(160);

export const PLAN_NODE_EXPECTED_ARTIFACT_KINDS = {
  createdWorkflow: 'createdWorkflow',
  documentSelection: 'documentSelection',
} as const;

export const PlanNodeExpectedArtifactKindSchema = z.enum(PLAN_NODE_EXPECTED_ARTIFACT_KINDS);
export type PlanNodeExpectedArtifactKind = z.infer<typeof PlanNodeExpectedArtifactKindSchema>;

export const PlanNodeExpectedArtifactSchema = z.object({
  kind: PlanNodeExpectedArtifactKindSchema,
  /**
   * `.min(1)`: empty reasons defeat the wrapping-object rationale (the
   * label exists so the planner gives each artifact a justification the
   * downstream agent can read). Required, non-empty.
   */
  reason: z.string().min(1),
});
export type PlanNodeExpectedArtifact = z.infer<typeof PlanNodeExpectedArtifactSchema>;

export const PlanNodeInputSchema = z.object({
  instructions: z.string().min(1),
});
export type PlanNodeInput = z.infer<typeof PlanNodeInputSchema>;

/**
 * One context-reference entry in a plan node's `contextFrom` array.
 * The wrapping object (vs. a bare number) is deliberate: with structured
 * output, models are noticeably more reliable picking the right upstream
 * when each reference is a labelled slot rather than a primitive in an array.
 *
 * `reason` is `.min(1)` because the labelled-slot rationale falls apart
 * the moment the planner can emit a blank justification — the field is
 * here so downstream agents can read a brief human-language explanation
 * of why each prior output is being threaded in.
 */
export const PlanNodeContextRefSchema = z.object({
  nodeId: z.number().int().positive(),
  reason: z.string().min(1),
});
export type PlanNodeContextRef = z.infer<typeof PlanNodeContextRefSchema>;

/**
 * NOTE on the three-state encoding of `contextFrom` on a plan node:
 *   - `null`                        → "default to previous stage outputs"
 *     (workflow falls back to the prevStageNodeIds policy)
 *   - `[]` (empty array)            → "no priors at all"
 *     (this node ignores the previous stage's outputs deliberately)
 *   - `[{ nodeId, reason }, ...]`   → "use exactly these"
 *
 * Three distinct meanings in one field. Kept this shape (not a tagged
 * union) because the LLM-side schema must match what `AgentPlanLLMSchema`
 * accepts, and OpenAI structured outputs are noticeably more reliable
 * with `array | null` than with a discriminated union here. Type-side
 * callers must use the helpers in the workflow / client to resolve the
 * effective ids — see `resolvePriorNodeIds` in the workflow and
 * `inferNodeStatus` on the client.
 */

function createPlanNodeSchema<TInputSchema extends z.ZodType>(
  input: TInputSchema,
  agentSchema: z.ZodType<HarnessAgentName> = HarnessAgentNameSchema
) {
  return z.object({
    agent: agentSchema,
    contextFrom: z.array(PlanNodeContextRefSchema).nullable(),
    expectedArtifacts: z.array(PlanNodeExpectedArtifactSchema),
    id: z.number().int().positive(),
    input,
    /**
     * Short user-facing label for the node, shown as the card title in
     * the chat-harness UI ("Search SEC rules", "Read attached contract").
     * The planner emits a 2-6 word action phrase per node. Nullable for
     * backward compatibility with plans persisted before the field was
     * introduced — the client falls back to a humanised agent slug.
     */
    title: z.string().nullable(),
  });
}

function createSinglePlanStageSchema<TNodeSchema extends z.ZodType>(node: TNodeSchema) {
  return z.object({
    id: z.number().int().positive(),
    node,
    type: z.literal(PLAN_STAGE_TYPES.single),
  });
}

function createParallelPlanStageSchema<TNodeSchema extends z.ZodType>(node: TNodeSchema) {
  return z.object({
    id: z.number().int().positive(),
    nodes: z.array(node).min(2),
    type: z.literal(PLAN_STAGE_TYPES.parallel),
  });
}

/**
 * Fields shared by storage and LLM node shapes. The two variants
 * differ only in the `input` slot (UUIDs vs indices).
 *
 * `contextFrom` is the explicit data-flow declaration: which upstream
 * node outputs should be passed as `priorOutputs` to this node. When
 * `null`, the workflow falls back to the default policy ("use the
 * previous stage's outputs"). Scheduling order is governed by the
 * containing stage, not by `contextFrom` — siblings inside a parallel
 * stage all start together regardless of their context references.
 */
export const BasePlanNodeSchema = createPlanNodeSchema(PlanNodeInputSchema);
export type PlanNode = z.infer<typeof BasePlanNodeSchema>;

const SinglePlanStageSchema = createSinglePlanStageSchema(BasePlanNodeSchema);
export type SinglePlanStage = z.infer<typeof SinglePlanStageSchema>;

const ParallelPlanStageSchema = createParallelPlanStageSchema(BasePlanNodeSchema);
export type ParallelPlanStage = z.infer<typeof ParallelPlanStageSchema>;

export const PlanStageSchema = z.discriminatedUnion('type', [
  SinglePlanStageSchema,
  ParallelPlanStageSchema,
]);
export type PlanStage = z.infer<typeof PlanStageSchema>;

export const AgentPlanSchema = z
  .object({
    /**
     * Concise user-facing summary of the overall plan. Optional so plans
     * persisted before this field was introduced remain valid.
     */
    planTitle: PlanTitleSchema.optional(),
    /**
     * Informational hint only — readers accept any string so older
     * persisted plans render correctly in chat history. The structural
     * validators below are what gate validity; a future contributor who
     * mistakes this for a real gate should rename to `schemaVersionHint`
     * or upgrade to a `z.literal(PLAN_SCHEMA_VERSION)` and add a
     * migration path for stored rows.
     */
    schemaVersion: z.string(),
    stages: z.array(PlanStageSchema).min(1),
    /**
     * Free-text stop-conditions emitted by the planner. `.min(1)` keeps
     * blank entries from showing up in the prompt — they confuse the
     * downstream agent and add no information.
     */
    stopConditions: z.array(z.string().min(1)),
    successCriteria: z.array(SuccessCriterionSchema).min(1),
    /**
     * The user's question for this turn. `.min(1)` mirrors the
     * client-side input validation; an empty `userQuery` here would
     * always indicate a serialization bug (we don't kick off plans for
     * blank prompts).
     */
    userQuery: z.string().min(1),
  })
  .superRefine((plan, ctx) => {
    runPlanRefinements(plan, ctx);
  });
export type AgentPlan = z.infer<typeof AgentPlanSchema>;

/**
 * The LLM-side node-input schema is byte-identical to the storage one
 * today; collapsed to a re-export so a future divergence is a single
 * named change rather than two parallel object literals that drifted
 * silently. If the LLM ever needs a different `input` shape (e.g.
 * indexed doc references instead of UUIDs), introduce a new schema
 * named after the divergence.
 */
export const PlanNodeInputLLMSchema = PlanNodeInputSchema;
export type PlanNodeInputLLM = PlanNodeInput;

export const BasePlanNodeLLMSchema = createPlanNodeSchema(PlanNodeInputLLMSchema);
export type PlanNodeLLM = z.infer<typeof BasePlanNodeLLMSchema>;

const SinglePlanStageLLMSchema = createSinglePlanStageSchema(BasePlanNodeLLMSchema);
const ParallelPlanStageLLMSchema = createParallelPlanStageSchema(BasePlanNodeLLMSchema);

/**
 * Do NOT use `z.discriminatedUnion` here. Zod emits JSON Schema `oneOf`
 * for discriminated unions, and OpenAI structured outputs rejects `oneOf`
 * inside `stages.items`. Plain `z.union` emits `anyOf`, which OpenAI
 * accepts for this planner response schema.
 */
export const PlanStageLLMSchema = z.union([SinglePlanStageLLMSchema, ParallelPlanStageLLMSchema]);
export type PlanStageLLM = z.infer<typeof PlanStageLLMSchema>;

/**
 * Builds the planner's structured-output schema for a given set of agents.
 * The agent enum is parameterized so an environment can gate an agent out of
 * the planner entirely (prompt AND schema together) — a feature-flagged agent
 * must be absent from BOTH or the model sees a valid-but-undescribed enum value
 * and gets confused. Defaults to every agent; pass a narrower enum to gate.
 */
export function createAgentPlanLLMSchema(
  agentSchema: z.ZodType<HarnessAgentName> = HarnessAgentNameSchema
) {
  const node = createPlanNodeSchema(PlanNodeInputLLMSchema, agentSchema);
  const stage = z.union([createSinglePlanStageSchema(node), createParallelPlanStageSchema(node)]);
  return z
    .object({
      planTitle: PlanTitleSchema.nullable(),
      stages: z.array(stage).min(1),
      stopConditions: z.array(z.string().min(1)),
      successCriteria: z.array(SuccessCriterionSchema).min(1),
      userQuery: z.string().min(1),
    })
    .superRefine((plan, ctx) => {
      runPlanRefinements(plan, ctx);
    });
}

export const AgentPlanLLMSchema = createAgentPlanLLMSchema();
export type AgentPlanLLM = z.infer<typeof AgentPlanLLMSchema>;

export function resolveAgentPlanFromLLM(planLLM: AgentPlanLLM): AgentPlan {
  function resolveNode(node: PlanNodeLLM): PlanNode {
    return {
      agent: node.agent,
      contextFrom: node.contextFrom,
      expectedArtifacts: node.expectedArtifacts,
      id: node.id,
      input: {
        instructions: node.input.instructions,
      },
      title: node.title,
    };
  }

  const resolvedStages: PlanStage[] = planLLM.stages.map(stage => {
    if (stage.type === 'single') {
      return {
        id: stage.id,
        node: resolveNode(stage.node),
        type: 'single',
      };
    }
    return {
      id: stage.id,
      nodes: stage.nodes.map(node => resolveNode(node)),
      type: 'parallel',
    };
  });

  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    stages: resolvedStages,
    stopConditions: planLLM.stopConditions,
    successCriteria: planLLM.successCriteria,
    userQuery: planLLM.userQuery,
    ...(planLLM.planTitle ? { planTitle: planLLM.planTitle } : {}),
  };
}

/**
 * flattenPlanNodes lives in `./flatten-plan-nodes` so the Temporal
 * worker bundle can import it without pulling in this file's runtime
 * zod chain.
 */
import { flattenPlanNodes, resolvePriorNodeIds } from './flatten-plan-nodes';
export type { FlattenedPlanNode } from './flatten-plan-nodes';
export { flattenPlanNodes, resolvePriorNodeIds };

/**
 * Find a node in the plan by id. Walks both single and parallel
 * stages. Returns null when no node matches.
 */
export function findNodeInPlan(plan: AgentPlan, nodeId: number): PlanNode | null {
  for (const stage of plan.stages) {
    if (stage.type === 'single') {
      if (stage.node.id === nodeId) return stage.node;
      continue;
    }
    for (const node of stage.nodes) {
      if (node.id === nodeId) return node;
    }
  }
  return null;
}

// ─── Shared refinements ──────────────────────────────────────────────

interface PlanShape<TInput> {
  stages: ReadonlyArray<
    | {
        id: number;
        node: PlanNodeShape<TInput>;
        type: 'single';
      }
    | {
        id: number;
        nodes: PlanNodeShape<TInput>[];
        type: 'parallel';
      }
  >;
}

interface PlanNodeShape<TInput> {
  agent: HarnessAgentName;
  contextFrom: PlanNodeContextRef[] | null;
  expectedArtifacts: PlanNodeExpectedArtifact[];
  id: number;
  input: TInput;
  title: string | null;
}

/**
 * Structural plan-shape refinements shared by both `AgentPlanSchema`
 * and `AgentPlanLLMSchema`.
 */
function runPlanRefinements<TInput>(plan: PlanShape<TInput>, ctx: z.RefinementCtx): void {
  addStageIdIssues(plan, ctx);
  addNodeIdIssues(plan, ctx);
  addContextFromReachIssues(plan, ctx);
  addFinalStageSingleIssues(plan, ctx);
}

/**
 * Walk a plan's nodes once, producing each node together with the
 * stage index and the zod path that points at it. Shared by every
 * refinement that needs to attach `ctx.addIssue` to a specific node —
 * previously the `single` vs `parallel` branching was inlined three
 * separate times across the refinements.
 */
function* iterPlanNodesWithPath<TInput>(
  plan: PlanShape<TInput>
): Generator<{ node: PlanNodeShape<TInput>; path: (string | number)[]; stageIndex: number }> {
  for (let stageIndex = 0; stageIndex < plan.stages.length; stageIndex++) {
    const stage = plan.stages[stageIndex];
    if (stage.type === 'single') {
      yield { node: stage.node, path: ['stages', stageIndex, 'node'], stageIndex };
      continue;
    }
    for (let nodeIndex = 0; nodeIndex < stage.nodes.length; nodeIndex++) {
      yield {
        node: stage.nodes[nodeIndex],
        path: ['stages', stageIndex, 'nodes', nodeIndex],
        stageIndex,
      };
    }
  }
}

/**
 * Stage ids must be 1..N strictly increasing with no duplicates.
 */
function addStageIdIssues<TInput>(plan: PlanShape<TInput>, ctx: z.RefinementCtx): void {
  const seen = new Set<number>();
  plan.stages.forEach((stage, index) => {
    const expected = index + 1;
    if (stage.id !== expected) {
      ctx.addIssue({
        code: 'custom',
        message: `Stage at index ${index} has id ${stage.id}; expected ${expected} (stage ids must be 1..N strictly increasing).`,
        path: ['stages', index, 'id'],
      });
    }
    if (seen.has(stage.id)) {
      ctx.addIssue({
        code: 'custom',
        message: `Duplicate stage id ${stage.id}.`,
        path: ['stages', index, 'id'],
      });
    }
    seen.add(stage.id);
  });
}

/**
 * Node ids must be globally unique and 1..M strictly increasing
 * across the flattened-in-stage-order node sequence. Same monotonicity
 * we had on the flat shape, just applied after the stage flatten.
 */
function addNodeIdIssues<TInput>(plan: PlanShape<TInput>, ctx: z.RefinementCtx): void {
  const seen = new Set<number>();
  let position = 0;
  for (const { node, path, stageIndex } of iterPlanNodesWithPath(plan)) {
    const expected = position + 1;
    if (node.id !== expected) {
      ctx.addIssue({
        code: 'custom',
        message: `Node at flattened position ${position} (stage ${plan.stages[stageIndex].id}) has id ${node.id}; expected ${expected} (node ids must be 1..M strictly increasing across the flattened stages).`,
        path: [...path, 'id'],
      });
    }
    if (seen.has(node.id)) {
      ctx.addIssue({
        code: 'custom',
        message: `Duplicate node id ${node.id}.`,
        path: [...path, 'id'],
      });
    }
    seen.add(node.id);
    position += 1;
  }
}

/**
 * Every `contextFrom` reference must point at a node in a strictly
 * earlier stage. Same-stage siblings and later-stage references are
 * rejected — the executor runs parallel siblings concurrently, so
 * they can't see each other's outputs.
 */
function addContextFromReachIssues<TInput>(plan: PlanShape<TInput>, ctx: z.RefinementCtx): void {
  const stageOfNode = new Map<number, number>();
  for (const { node, stageIndex } of iterPlanNodesWithPath(plan)) {
    stageOfNode.set(node.id, stageIndex);
  }

  for (const { node, path, stageIndex } of iterPlanNodesWithPath(plan)) {
    if (!node.contextFrom) continue;
    node.contextFrom.forEach((ref, refIndex) => {
      const referencedStage = stageOfNode.get(ref.nodeId);
      if (referencedStage == null) {
        ctx.addIssue({
          code: 'custom',
          message: `Node ${node.id} contextFrom[${refIndex}].nodeId = ${ref.nodeId} refers to a node that doesn't exist in the plan.`,
          path: [...path, 'contextFrom', refIndex, 'nodeId'],
        });
        return;
      }
      if (referencedStage >= stageIndex) {
        ctx.addIssue({
          code: 'custom',
          message: `Node ${node.id} contextFrom[${refIndex}].nodeId = ${ref.nodeId} refers to a node in stage ${referencedStage + 1}; contextFrom must reference a strictly earlier stage (this node is in stage ${stageIndex + 1}).`,
          path: [...path, 'contextFrom', refIndex, 'nodeId'],
        });
      }
    });
  }
}

/**
 * The final stage MUST be `type: 'single'`. Every harness agent produces
 * user-visible text, so the remaining terminal invariant is one final node
 * instead of a parallel fan-out with no clear final step.
 */
function addFinalStageSingleIssues<TInput>(plan: PlanShape<TInput>, ctx: z.RefinementCtx): void {
  const lastIndex = plan.stages.length - 1;
  if (lastIndex < 0) return;
  const lastStage = plan.stages[lastIndex];
  if (lastStage.type !== 'single') {
    ctx.addIssue({
      code: 'custom',
      message:
        'Final stage must be type "single" so the plan terminates on exactly one user-facing node. Parallel outputs must feed one terminal single-stage node when a final recap or synthesis is needed.',
      path: ['stages', lastIndex, 'type'],
    });
  }
}
